import { useQuery } from "@apollo/client";
import {
  ErrorOutline,
  LightbulbOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { Box, Button, styled, Typography } from "@mui/material";
import { ReactElement } from "react";
import { Outlet } from "react-router-dom";

import {
  ListActiveBannersForTenantQuery,
  ListActiveBannersForSubaccountQuery,
  SubaccountBudgetStateStatus,
  BannerLevel,
  UserTenantRole,
} from "~frontend/__generated__/graphql";
import {
  LIST_ACTIVE_BANNERS_FOR_SUBACCOUNT,
  LIST_ACTIVE_BANNERS_FOR_TENANT,
} from "~frontend/graphql/banners";
import BreadcrumbsProvider from "~frontend/providers/BreadcrumbsProvider";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";
import {
  useCurrentUser,
  useCurrentUserSubaccountMemberships,
  UserSubaccountMembership,
} from "~frontend/providers/LoggedInUserProvider/context";
import {
  subaccountLandingPage,
  useSubaccount,
  useSubaccountAdminConfig,
} from "~frontend/providers/SubaccountProvider";
import { formatMillicentsAsDollars } from "~frontend/routes/organization/pricingV1/priceFormatting";
import { palette } from "~frontend/theme";
import { getLastUsedSubaccountId } from "~frontend/util/subaccountUsage";

import { FlexBox } from "./FlexBox";
import Sidebar, {
  collapsedWidth,
  expandedWidth,
  PrimarySidebarItem,
  SidebarItem,
  useCollapsedState,
} from "./Sidebar";
import TopBar from "./TopBar";

export const ContentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  overflow: "auto",
  marginTop: theme.layout.topBarHeight,
}));

export const PageWrapper = styled("div")({
  flex: "1 1 auto",
  backgroundColor: palette.lightestGray,
  minHeight: "calc(100vh - 50px)",
});

const BANNER_LEVEL_MAPPING = {
  INFO: {
    backgroundColor: palette.lightBlue,
    color: palette.navy,
    icon: <LightbulbOutlined />,
    label: "Note",
    linkHoverColor: palette.white,
    linkHoverBackgroundColor: palette.navy,
  },
  WARNING: {
    backgroundColor: palette.lightOrange,
    color: palette.navy,
    icon: <WarningAmberOutlined />,
    label: "Warning",
    linkHoverColor: palette.white,
    linkHoverBackgroundColor: palette.navy,
  },
  ERROR: {
    backgroundColor: palette.red,
    color: palette.white,
    icon: <ErrorOutline />,
    label: "Error",
    linkHoverColor: palette.red,
    linkHoverBackgroundColor: palette.white,
  },
};

type BannerProps = {
  key: string;
  message: string;
  level: "INFO" | "WARNING" | "ERROR";
  centered?: boolean;
  hideIcon?: boolean;
  hideLabel?: boolean;
  link?: string | null;
  linkText?: string | null;
};

const Banner = ({
  message,
  link,
  linkText,
  level,
  centered,
  hideIcon,
  hideLabel,
}: BannerProps) => {
  const {
    backgroundColor,
    color,
    icon,
    label,
    linkHoverColor,
    linkHoverBackgroundColor,
  } = BANNER_LEVEL_MAPPING[level];
  return (
    <Box
      sx={{
        width: "100%",
        color,
        backgroundColor,
        py: 2,
        px: 5,
      }}
    >
      <Box sx={{ maxWidth: "1200px", m: "auto" }}>
        <FlexBox justifyContent={centered ? "center" : "space-between"} gap={2}>
          <FlexBox alignItems="center" gap="10px">
            {!hideIcon && icon}
            <Typography variant="body2">
              {!hideLabel && (
                <>
                  <strong>{label}:</strong>{" "}
                </>
              )}
              {message}
            </Typography>
          </FlexBox>
          {link && linkText && (
            <Box>
              <Button
                variant="outlined"
                href={link}
                color="inherit"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  minWidth: "100px",
                  ":hover": {
                    color: linkHoverColor,
                    backgroundColor: linkHoverBackgroundColor,
                    borderColor: linkHoverBackgroundColor,
                  },
                }}
              >
                <Typography variant="h4Caps">{linkText}</Typography>
              </Button>
            </Box>
          )}
        </FlexBox>
      </Box>
    </Box>
  );
};

export default function Layout({
  topBarIcon,
  topBarTitle,
  topBarLink,
  sidebarItems,
  sidebarSecondaryItems = [],
}: {
  topBarIcon?: ReactElement;
  topBarTitle: string;
  topBarLink: string;
  sidebarItems: PrimarySidebarItem[];
  sidebarSecondaryItems?: SidebarItem[];
}): ReactElement {
  const currentUser = useCurrentUser();
  const subaccount = useSubaccount();
  const subaccountAdminConfig = useSubaccountAdminConfig();
  const [collapsed, setCollapsed] = useCollapsedState();

  const showSidebar = sidebarItems.length + sidebarSecondaryItems.length > 1;

  const { data: subaccountBanners } =
    useQuery<ListActiveBannersForSubaccountQuery>(
      LIST_ACTIVE_BANNERS_FOR_SUBACCOUNT,
      {
        variables: {
          input: {
            subaccountId: subaccount?.subaccount?.id,
          },
        },
        pollInterval: 60000,
        skip: !subaccount?.subaccount?.id || subaccount?.role === "TEXTER",
      }
    );

  const { data: tenantBanners } = useQuery<ListActiveBannersForTenantQuery>(
    LIST_ACTIVE_BANNERS_FOR_TENANT,
    {
      pollInterval: 60000,
      skip: !!subaccount?.subaccount?.id || subaccount?.role === "TEXTER",
    }
  );

  const banners: BannerProps[] = [];

  if (subaccount?.subaccount) {
    banners.push(
      ...(subaccountBanners?.listActiveBannersForSubaccount.map(b => ({
        ...b,
        key: b.id,
      })) ?? [])
    );
    if (subaccountAdminConfig) {
      const userIsOrgOwnerOrHigher =
        currentUser?.tenantRole !== UserTenantRole.None;
      if (
        subaccountAdminConfig?.budgetStatus.status ===
        SubaccountBudgetStateStatus.Warning
      ) {
        banners.push({
          key: "budget-warning",
          level: BannerLevel.Warning,
          message: `There is only $${formatMillicentsAsDollars(subaccountAdminConfig.budgetStatus.balanceInMillicents)} left in your budget. If you exceed your budget, all messaging will be paused until the budget is increased.`,
          linkText: userIsOrgOwnerOrHigher
            ? "Increase Your Budget"
            : "Contact Your Organization Owners",
          link: userIsOrgOwnerOrHigher
            ? "/organization/budgets"
            : `mailto:${subaccountAdminConfig.budgetStatus.orgOwners!.map(oo => oo.email).join(",")}`,
        });
      } else if (
        subaccountAdminConfig?.budgetStatus.status ===
        SubaccountBudgetStateStatus.Exceeded
      ) {
        banners.push({
          key: "budget-warning",
          level: BannerLevel.Error,
          message: `You have exceeded your budget on this subaccount. All messaging will be paused until the budget is increased.`,
          linkText: userIsOrgOwnerOrHigher
            ? "Increase Your Budget"
            : "Contact Your Organization Owners",
          link: userIsOrgOwnerOrHigher
            ? "/organization/budgets"
            : `mailto:${subaccountAdminConfig.budgetStatus.orgOwners!.map(oo => oo.email).join(",")}`,
        });
      }
    } else if (subaccount.subaccount.sendingDisabled) {
      // user is supervol or texter
      banners.push({
        key: "sending-disabled-warning",
        level: BannerLevel.Warning,
        message:
          "Messaging has been paused on this subaccount. Please contact your organization owner for more information.",
        centered: true,
        hideIcon: true,
        hideLabel: true,
      });
    }
  } else {
    banners.push(
      ...(tenantBanners?.listActiveBannersForTenant.map(b => ({
        ...b,
        key: b.id,
      })) ?? [])
    );
  }

  return (
    <div style={{ display: "flex", height: "100%" }}>
      <BreadcrumbsProvider>
        {showSidebar ? (
          <Sidebar
            primaryItems={sidebarItems}
            secondaryItems={sidebarSecondaryItems}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
          />
        ) : null}
        <ContentWrapper>
          <TopBar
            icon={topBarIcon}
            title={topBarTitle}
            titleLink={topBarLink}
            leftOffset={
              showSidebar ? (collapsed ? collapsedWidth : expandedWidth) : "0px"
            }
          />

          <PageWrapper>
            <FlexBox direction="column" gap="2px">
              {(banners || []).map(banner => (
                <Banner
                  key={banner.key}
                  message={banner.message}
                  link={banner.link ?? undefined}
                  linkText={banner.linkText ?? undefined}
                  level={banner.level}
                  centered={banner.centered}
                  hideIcon={banner.hideIcon}
                  hideLabel={banner.hideLabel}
                />
              ))}
            </FlexBox>
            <Outlet />
          </PageWrapper>
        </ContentWrapper>
      </BreadcrumbsProvider>
    </div>
  );
}

export function useBackToSubaccountListItem(): [SidebarItem] | [] {
  const tenant = useTenant()!;
  const memberships = useCurrentUserSubaccountMemberships()!;
  const lastUsedSubaccountId = getLastUsedSubaccountId();
  const intl = useIntl();

  let backToSubaccount: UserSubaccountMembership | undefined;
  if (memberships.length === 1) {
    backToSubaccount = memberships[0];
  } else if (lastUsedSubaccountId) {
    backToSubaccount = memberships.find(
      m => m.subaccount.id === lastUsedSubaccountId
    );
  }

  if (!backToSubaccount) {
    return [];
  }

  return [
    {
      label: intl.formatMessage(
        {
          defaultMessage: "Back to {subaccountName}",
          description: "Back to subaccount link in sidebar",
        },
        {
          subaccountName: backToSubaccount.subaccount.name,
        }
      ),
      path: tenant.subaccountsEnabled
        ? `/subaccounts/${backToSubaccount.subaccount.slug}/${subaccountLandingPage(
            backToSubaccount
          )}`
        : `/admin/${subaccountLandingPage(backToSubaccount)}`,
    },
  ];
}
